import { List, Stack, Typography } from "@mui/material";
import type { CommunityResponse } from "@bw/shared/types/generated";
import { DenseRow } from "../../../common/components/DenseRow";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { useCommunities } from "../hooks";
import { useCommunityHubActivity } from "../hooks/useCommunityHubActivity";
import { CommunityAvatar } from "./CommunityAvatar";

const RAIL_LIMIT = 5;

/**
 * Desktop rail: your communities with the one fact worth a glance each,
 * unread chat first, else the next event. Server payloads only.
 */
export function YourCommunitiesRail() {
  const communitiesQ = useCommunities();
  const joined: CommunityResponse[] = (communitiesQ.data ?? []).filter((community) => community.is_member);
  const hub = useCommunityHubActivity(joined);
  if (communitiesQ.isLoading || communitiesQ.isError || joined.length === 0) return null;
  const nextEvent = new Map<string, string>();
  for (const { community, event } of hub.upcoming) {
    if (!nextEvent.has(community.community_id)) nextEvent.set(community.community_id, event.title);
  }
  return (
    <Stack component="section" aria-labelledby="rail-communities" spacing={0.5}>
      <SectionHeader id="rail-communities" title="Your communities" action={{ label: "All", to: "/community", ariaLabel: "All communities" }} />
      <List disablePadding>
        {joined.slice(0, RAIL_LIMIT).map((community) => {
          const unread = hub.unreadByCommunity.get(community.community_id) ?? 0;
          const next = nextEvent.get(community.community_id);
          return (
            <DenseRow
              key={community.community_id}
              to={`/community/communities/${community.community_id}`}
              leading={<CommunityAvatar communityId={community.community_id} name={community.name} size={40} ring={unread > 0} />}
              title={community.name}
              subtitle={
                unread > 0 ? (
                  <Typography component="span" variant="body2" sx={{ color: "primary.main", fontWeight: 600 }}>
                    {unread} unread
                  </Typography>
                ) : next ? (
                  `Next: ${next}`
                ) : (
                  "No upcoming events"
                )
              }
            />
          );
        })}
      </List>
    </Stack>
  );
}
